// ═══════════════════════════════════════════════════════════════
// TemplateProvisionModal.tsx – Sablon-változók kezelése egy szervezetnél
// A szervezet környezeti (sablon-) változói: szerkesztés, JSON betöltése, mentés.
// A közzétételi lista betöltése / átállása / újraírása a Szervezetek → „Sablon – batch mód”
// ablakban történik (több szervezetre is), új szervezetnél pedig automatikusan.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ApiClient } from '../../api/client';
import type { TemplateDefinition } from '../../types';
import { TemplateVariablesEditor, type CustomVariable } from './TemplateVariablesEditor';
import { invalidateTemplateVariables } from '../../hooks/useTemplateVariables';
import { Sparkles, AlertCircle, X, RefreshCw, Save } from 'lucide-react';

interface TemplateProvisionModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId?: number;
  tenantName?: string;
  onSuccess?: () => void;
}

export const TemplateProvisionModal: React.FC<TemplateProvisionModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  tenantName,
  onSuccess,
}) => {
  const [loadingDef, setLoadingDef] = useState(false);
  const [definition, setDefinition] = useState<TemplateDefinition | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // melyik szervezet adatai vannak a mezőkben (null = újranyitáskor friss betöltés)
  const varsTenantRef = useRef<string | null>(null);
  const [customVars, setCustomVars] = useState<CustomVariable[]>([]);
  const [baseline, setBaseline] = useState<Record<string, string>>({});
  const [customBaseline, setCustomBaseline] = useState<CustomVariable[]>([]);
  const [savingVars, setSavingVars] = useState(false);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) varsTenantRef.current = null;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && !savingVars) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, savingVars, onClose]);

  useEffect(() => {
    if (isOpen) {
      loadTemplateDef();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tenantId]);

  const loadTemplateDef = async () => {
    setLoadingDef(true);
    setErrorMsg(null);
    setVariables({});
    setDefinition(null);
    const res = await ApiClient.getTemplateDefinition(tenantId, '2026');
    // a backend a mezőket a válasz gyökerében adja (nincs `data` boríték) – mindkét formát elfogadjuk
    const def = res.ok ? ((res.data ?? res) as TemplateDefinition) : null;
    if (def && (def.chapters || def.suggestedVariables)) {
      setDefinition(def);
      setVariables(def.suggestedVariables || {});
      setCustomVars(def.customVariables || []);
      setCustomBaseline(def.customVariables || []);
      setBaseline(def.suggestedVariables || {});
      setSavedNotice(null);
      varsTenantRef.current = `${tenantId ?? ''}`;
    } else {
      setErrorMsg('Nem sikerült betölteni a szervezet változóit');
    }
    setLoadingDef(false);
  };

  const handleFieldChange = (key: string, value: string) => {
    setVariables(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveVariables = async () => {
    setSavingVars(true);
    setErrorMsg(null);
    setSavedNotice(null);
    const res = await ApiClient.saveTemplateVariables(tenantId, variables, customVars);
    const payload = (res.ok ? (res.data ?? res) : null) as any;
    if (payload?.suggestedVariables) {
      setDefinition((d) => (d ? { ...d, savedKeys: payload.savedKeys, customVariables: payload.customVariables, variableDefinitions: payload.variableDefinitions } : d));
      setVariables(payload.suggestedVariables);
      setBaseline(payload.suggestedVariables);
      setCustomVars(payload.customVariables || []);
      setCustomBaseline(payload.customVariables || []);
      invalidateTemplateVariables();
      setSavedNotice(`Elmentve – ${payload.savedKeys.length} egyedi érték a szervezetnél. Legközelebb ezek töltődnek be, és a letöltött ZIP json-ja is ezeket tartalmazza.`);
      if (onSuccess) onSuccess();
    } else {
      setErrorMsg(res.error || 'Nem sikerült menteni a változókat');
    }
    setSavingVars(false);
  };

  const canSave = Boolean(definition?.canSaveVariables);

  if (!isOpen) return null;

  return createPortal(
    <div
      data-esc-self
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget && !savingVars) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpm-title"
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col my-auto max-h-[90vh] animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Fejléc */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-gradient-to-r from-blue-50/50 via-indigo-50/30 to-slate-50/50 dark:from-blue-950/30 dark:via-indigo-950/20 dark:to-slate-900">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center font-bold shadow-md shadow-indigo-500/20">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 id="tpm-title" className="text-base font-bold text-slate-900 dark:text-white">
                Sablon-változók
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {tenantName ? `${tenantName} – környezeti változók` : 'A szervezet környezeti változói'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={savingVars}
            aria-label="Bezárás"
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Tartalom */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1">
          {loadingDef ? (
            <div className="py-16 text-center text-slate-400 flex flex-col items-center justify-center gap-3">
              <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
              <span className="text-xs">Szervezeti változók betöltése...</span>
            </div>
          ) : (
            <form
              onSubmit={(e) => { e.preventDefault(); if (canSave) handleSaveVariables(); }}
              className="space-y-5"
            >
              {errorMsg && (
                <div role="alert" className="p-3 rounded-xl text-xs font-medium bg-rose-50 text-rose-800 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}

              {/* Szervezeti változók – a katalógusból, szervezetenként menthetők; JSON betöltése */}
              <TemplateVariablesEditor
                definitions={definition?.variableDefinitions || []}
                customVars={customVars}
                onCustomVarsChange={setCustomVars}
                values={variables}
                onChange={handleFieldChange}
                onBulkChange={(patch) => setVariables((prev) => ({ ...prev, ...patch }))}
                baseline={baseline}
                customBaseline={customBaseline}
                derived={definition?.derivedVariables || {}}
                savedKeys={definition?.savedKeys || []}
                tenantSlug={definition?.tenant?.slug}
                canSave={canSave}
                saving={savingVars}
                onSave={handleSaveVariables}
                savedNotice={savedNotice}
              />

              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                A közzétételi lista betöltése, az átállás a 2026-os listára és a teljes újraírás a Szervezetek fejlécében, a „Sablon – batch mód” ablakban érhető el.
              </p>

              {/* Gombok */}
              <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  disabled={savingVars}
                  onClick={onClose}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition cursor-pointer"
                >
                  Bezárás
                </button>
                <button
                  type="submit"
                  disabled={savingVars || !canSave || !definition}
                  title={canSave ? 'A változók mentése ennél a szervezetnél' : 'A mentéshez szervezeti admin jogosultság kell'}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl text-xs font-semibold shadow-md shadow-indigo-500/20 transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {savingVars ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span>{savingVars ? 'Mentés...' : 'Mentés'}</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
